import { Popconfirm } from "antd";
import type { ButtonProps } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import React from "react";

/** Types repris directement d'antd pour rester alignés sur la version installée. */
type PopConfirmProps = React.ComponentProps<typeof Popconfirm>;
type OkType = PopConfirmProps["okType"];
type Placement = PopConfirmProps["placement"];

type CustomPopConfirmType = {
  /** Message principal. Peut être coloré via messageColor. */
  title?: React.ReactNode;
  /** Contenu additionnel sous le message (ex. un champ de saisie). */
  description?: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  okText?: string;
  cancelText?: string;
  children?: React.ReactNode;
  /**
   * Couleur du message d'avertissement : appliquée au titre et à l'icône par
   * défaut, pour adapter le ton de la confirmation (ex. rouge sur une
   * annulation, orange sur un avertissement).
   */
  messageColor?: string;
  /** Icône personnalisée ; par défaut l'icône d'avertissement d'antd, teintée par messageColor. */
  icon?: React.ReactNode;
  /** Style du bouton de confirmation, "danger" pour une action destructive. */
  okType?: OkType;
  /** État du bouton de confirmation (loading, disabled...). */
  okButtonProps?: ButtonProps;
  placement?: Placement;
  /**
   * Appelé quand antd demande un changement d'ouverture (clic à l'extérieur,
   * échap...). Permet de refermer une confirmation contrôlée par isOpen.
   */
  onOpenChange?: (open: boolean) => void;
};

export const CustomPopConfirm = ({
  isOpen,
  onClose,
  onConfirm,
  cancelText,
  children,
  okText,
  title,
  description,
  messageColor,
  icon,
  okType,
  okButtonProps,
  placement = "top",
  onOpenChange,
}: CustomPopConfirmType) => {
  const messageIcon =
    icon ??
    (messageColor ? (
      <ExclamationCircleFilled style={{ color: messageColor }} />
    ) : undefined);

  return (
    <Popconfirm
      title={
        title ? (
          <span style={messageColor ? { color: messageColor } : undefined}>
            {title}
          </span>
        ) : undefined
      }
      description={description}
      open={isOpen}
      icon={messageIcon}
      okText={okText ?? "Oui"}
      cancelText={cancelText ?? "Non"}
      okType={okType}
      okButtonProps={okButtonProps}
      placement={placement}
      onOpenChange={onOpenChange}
      onCancel={onClose}
      onConfirm={onConfirm}
    >
      {children}
    </Popconfirm>
  );
};
